import React, { useEffect, useState } from 'react';
import { Box, HStack, IconButton } from '@chakra-ui/react';
import { Stack, Text } from '@chakra-ui/layout';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FiPlusCircle, FiTrash, FiXCircle } from 'react-icons/all';
import Icon from '@chakra-ui/icon';
import { Button, Datepicker, Heading, Input, Table } from '~/components';
import { FileUploader } from '~/components/FileInput';
import { InputFloat } from '~/components/InputFloat';
import { ZeroCarbonProjectInvoice } from '~/types/zeroCarbon';

const ProjectFormSchema = yup.object().shape({
  nmr_nota_fiscal: yup
    .string()
    .required('Número da nota fiscal obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  data_compra: yup.string().required('Data da compra obrigatória'),
  valor: yup.number().required('Valor obrigatório'),
  anexo_nota_fiscal_nome: yup.string().required('Anexo obrigatório'),
});

interface ProjectInvoiceProps {
  onChangeList: (
    notas_fiscais: ZeroCarbonProjectInvoice[],
    possuiErros: boolean,
  ) => void;
  notas_fiscais?: ZeroCarbonProjectInvoice[];
  isFetching?: boolean;
}

export const ProjectInvoice: React.VFC<ProjectInvoiceProps> = ({
  notas_fiscais,
  onChangeList,
  isFetching = false,
}) => {
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm({
    resolver: yupResolver(ProjectFormSchema),
  });
  const { errors } = formState;
  const [notasFiscais, setNotasFiscais] = useState<ZeroCarbonProjectInvoice[]>(
    notas_fiscais?.length
      ? notas_fiscais
      : ([
          {
            numero: '',
            data_compra: undefined,
            valor: undefined,
            quantidade_co2_contratado: 0,
            anexo_nota_fiscal_nome: '',
            anexo_nota_fiscal: undefined,
          },
        ] as ZeroCarbonProjectInvoice[]),
  );

  useEffect(() => {
    onChanges();
  }, [notasFiscais]);

  const onChanges = () => {
    const erros = notasFiscais.some(obj => {
      return Object.values(obj).some(
        value => value === undefined || value === 0 || value === '',
      );
    });
    onChangeList(notasFiscais, erros);
  };

  const limparAnexoNotaFiscal = (index: number) => {
    const nota = notasFiscais[index];
    setNotasFiscais([
      ...notasFiscais.slice(0, index),
      { ...nota, anexo_nota_fiscal: undefined, anexo_nota_fiscal_nome: '' },
      ...notasFiscais.slice(index + 1),
    ]);
    setError(`anexo_nota_fiscal_nome-${index}`, {
      type: 'required',
      message: 'Anexo obrigatório',
    });
  };

  return (
    <Stack>
      {notasFiscais.map((nota, index) => (
        <HStack gridGap={3} alignItems="top" key={`${index + 1}-nota`}>
          <Input
            value={nota.numero}
            label="N° da Nota fiscal"
            name={`nmr_nota_fiscal-${index}`}
            isRequired
            type="text"
            error={errors[`nmr_nota_fiscal-${index}`]}
            onChange={e => {
              setNotasFiscais([
                ...notasFiscais.slice(0, index),
                { ...nota, numero: e.target.value },
                ...notasFiscais.slice(index + 1),
              ]);
              if (!e.target.value.trim()) {
                setError(`nmr_nota_fiscal-${index}`, {
                  type: 'required',
                  message: 'Número da nota fiscal obrigatório',
                });
              } else if (e.target.value.trim().length > 255) {
                setError(`nmr_nota_fiscal-${index}`, {
                  type: 'max',
                  message: 'Máximo de 255 caracteres',
                });
              } else {
                clearErrors(`nmr_nota_fiscal-${index}`);
              }
            }}
            isDisabled={isFetching}
          />
          <Datepicker
            label="Data da compra"
            required
            onChange={e => {
              setNotasFiscais([
                ...notasFiscais.slice(0, index),
                { ...nota, data_compra: e },
                ...notasFiscais.slice(index + 1),
              ]);
              if (!e) {
                setError(`data_compra-${index}`, {
                  type: 'required',
                  message: 'Data da compra obrigatória',
                });
              } else {
                clearErrors(`data_compra-${index}`);
              }
            }}
            name={`data_compra-${index}`}
            selectedDate={nota.data_compra}
            error={errors[`data_compra-${index}`]}
            isDisabled={isFetching}
          />
          <InputFloat
            value={nota.valor}
            label="Valor (R$)"
            name={`valor-${index}`}
            isRequired
            error={errors[`valor-${index}`]}
            onChangeValue={e => {
              setNotasFiscais([
                ...notasFiscais.slice(0, index),
                { ...nota, valor: e },
                ...notasFiscais.slice(index + 1),
              ]);
              if (e === 0) {
                setError(`valor-${index}`, {
                  type: 'required',
                  message: 'Valor obrigatório',
                });
              } else {
                clearErrors(`valor-${index}`);
              }
            }}
            isDisabled={isFetching}
          />
          <Input
            value={Number(nota.quantidade_co2_contratado).toLocaleString(
              'pt-BR',
              {
                maximumFractionDigits: 0,
              },
            )}
            label="Quantidade de CO2e (t)"
            name={`qtde_co2e-${index}`}
            isRequired
            type="text"
            inputMode="numeric"
            error={errors[`qtde_co2e-${index}`]}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, '');
              setNotasFiscais([
                ...notasFiscais.slice(0, index),
                { ...nota, quantidade_co2_contratado: Number(value) },
                ...notasFiscais.slice(index + 1),
              ]);
              if (Number(e.target.value) === 0) {
                setError(`qtde_co2e-${index}`, {
                  type: 'required',
                  message: 'Quantidade de CO2e obrigatória',
                });
              } else {
                clearErrors(`qtde_co2e-${index}`);
              }
            }}
            isDisabled={isFetching}
          />
          <FileUploader
            isRequired
            fileName={nota.anexo_nota_fiscal_nome}
            onChange={file => {
              setNotasFiscais([
                ...notasFiscais.slice(0, index),
                {
                  ...nota,
                  anexo_nota_fiscal: file,
                  anexo_nota_fiscal_nome: file.name,
                },
                ...notasFiscais.slice(index + 1),
              ]);
              if (!file) {
                setError(`anexo_nota_fiscal_nome-${index}`, {
                  type: 'required',
                  message: 'Anexo obrigatório',
                });
              } else {
                clearErrors(`anexo_nota_fiscal_nome-${index}`);
              }
            }}
            isDisabled={isFetching}
            clearFile={() => limparAnexoNotaFiscal(index)}
            label="Anexar nota fiscal"
            showButton={false}
            helpText="Arquivo do tipo pdf. O limite total do arquivo é de 5MB."
            errors={errors[`anexo_nota_fiscal_nome-${index}`]}
          />
          {index > 0 && (
            <IconButton
              marginTop="30px !important"
              aria-label="Remover nota fiscal"
              icon={<Icon as={FiXCircle} />}
              onClick={() =>
                setNotasFiscais(notasFiscais.filter((_, i) => i !== index))
              }
              isDisabled={isFetching}
            />
          )}
          {index === 0 && (
            <IconButton
              marginTop="30px !important"
              aria-label="Adicionar nota fiscal"
              icon={<Icon as={FiPlusCircle} />}
              onClick={() =>
                setNotasFiscais([
                  ...notasFiscais,
                  {
                    numero: '',
                    data_compra: undefined,
                    valor: 0,
                    quantidade_co2_contratado: 0,
                    anexo_nota_fiscal_nome: '',
                    anexo_nota_fiscal: undefined,
                  },
                ])
              }
              isDisabled={isFetching}
            />
          )}
        </HStack>
      ))}
    </Stack>
  );
};
